// VOA (and most imported) addresses arrive fully UPPERCASE from the source
// data in the backend. That's shouty to read in the leads UI, so we title-case
// for DISPLAY only - the raw value is still what we persist, geocode and match
// against. Left uppercase: any token that contains a digit (UK postcode
// fragments like "BS11"/"6FB", house numbers like "12A", road refs like "A1" -
// titlecasing a postcode reads wrong and digits carry no case), plus common
// all-caps business suffixes, so "NISBETTS PLC, BS11 6FB" reads
// "Nisbetts PLC, BS11 6FB", not "Nisbetts Plc, Bs11 6Fb".
//
// This is a best-effort cosmetic transform: genuine acronyms in a company name
// (e.g. "PPG") can't be detected generically and will title-case. Extend
// KEEP_UPPER as real cases surface.

const KEEP_UPPER = new Set([
  "PLC",
  "LTD",
  "LLP",
  "LP",
  "LLC",
  "UK",
  "CIC",
  "NHS",
  "BP",
  "DIY",
  "TV",
]);

export const formatAddressCasing = (raw: string | null | undefined): string => {
  if (!raw) return raw ?? "";
  // Only transform strings that are (near) fully uppercase; a value already in
  // mixed case was likely typed normally and shouldn't be clobbered.
  const letters = raw.replace(/[^A-Za-z]/g, "");
  if (letters && letters !== letters.toUpperCase()) return raw;
  // Tokenise alphanumeric runs (incl. digits) so postcode fragments reach the
  // "has a digit -> keep as-is" guard as whole tokens rather than being split
  // into a letter half that gets title-cased.
  return raw.replace(/[A-Za-z0-9]+(?:'[A-Za-z0-9]+)?/g, (word) => {
    // Any token with a digit stays in its original (upper) case: postcodes,
    // house numbers, unit codes. The input is already uppercase here, so
    // "keep as-is" preserves the shouty-but-correct casing for these.
    if (/\d/.test(word)) return word;
    const upper = word.toUpperCase();
    if (KEEP_UPPER.has(upper)) return upper;
    // Single letters (e.g. the "B"/"Q" in "B & Q") stay uppercase.
    if (word.length === 1) return upper;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
};
